import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import type { RetailOrderLineItem } from '~/api/graphql/generated/types'
import { PrescriptionType } from '~/constants/prescription'
import { PrescriptionItemState } from '~/constants/SOAPStates'
import { useIsChewyCheckoutEnabled } from '~/store/hooks/business'
import {
  useGetPrescriptionStateId,
  useOrderStateId,
} from '~/store/hooks/orders'
import type { InvoiceLineItem } from '~/types'
import { getIsRetailOrderLineItem } from '~/utils/invoiceUtils'
import { getPrescriptionType } from '~/utils/prescription'

import { useIsItemPrescribable, useIsItemVaccine } from './useItemProperties'

const shouldDisplayChewyIcon = (
  item: InvoiceLineItem | RetailOrderLineItem,
  isRetailOrderLineItem: boolean,
  isChewyCheckoutEnabled: boolean,
): boolean => {
  if (isRetailOrderLineItem) {
    return isChewyCheckoutEnabled
  }

  const invoiceItem = item as InvoiceLineItem
  const { isChewyActiveRx, isChewyReactiveRx } = getPrescriptionType(
    invoiceItem.prescriptionType,
    invoiceItem.origin,
  )

  return (isChewyActiveRx || isChewyReactiveRx) && isChewyCheckoutEnabled
}

export interface UsePrescriptionIconLogicParams {
  isBundle: boolean
  item: InvoiceLineItem | RetailOrderLineItem
  onVaccineClick?:
    | ((item: InvoiceLineItem | RetailOrderLineItem) => void)
    | null
}

export interface UsePrescriptionIconLogicResult {
  displayChewyIcon: boolean
  isPrescribable: boolean
  isVaccine: boolean
  shouldShowChewyIcon: boolean
  shouldShowWarningIcon: boolean
  warningTooltipText: string
}

export const usePrescriptionIconLogic = ({
  item,
  isBundle,
  onVaccineClick,
}: UsePrescriptionIconLogicParams): UsePrescriptionIconLogicResult => {
  const { t } = useTranslation()
  const isRetailOrderLineItem = getIsRetailOrderLineItem(item)
  const isChewyCheckoutEnabled = useIsChewyCheckoutEnabled()

  // Get hooks for checking prescription state
  const getPrescriptionStateId = useGetPrescriptionStateId()
  const orderStateId = useOrderStateId(item)
  const approvedStateId = getPrescriptionStateId(PrescriptionItemState.APPROVED)
  const isApprovedStatus = orderStateId === approvedStateId

  // Determine if item is prescribable
  const isPrescribable = useIsItemPrescribable(
    item,
    isRetailOrderLineItem,
    isBundle,
  )

  // Determine if item is a vaccine
  const isVaccine = useIsItemVaccine(item, isRetailOrderLineItem, isBundle)

  return useMemo(() => {
    // Determine if we should display Chewy icon
    const displayChewyIcon = shouldDisplayChewyIcon(
      item,
      isRetailOrderLineItem,
      isChewyCheckoutEnabled,
    )

    // Determine if prescription type is Chewy
    // Use displayChewyIcon to determine if it's Chewy (already checks type + origin + checkout enabled)
    // But also check prescriptionType directly for tooltip logic (needed even if checkout is disabled)
    const isChewyPrescriptionTypeForLogic = displayChewyIcon // For warning/icon display logic
    const isChewyPrescriptionTypeForTooltip = !isRetailOrderLineItem
      ? (item as InvoiceLineItem).prescriptionType === PrescriptionType.CHEWY
      : displayChewyIcon // For retail order items, if Chewy icon is displayed, it's Chewy

    // Determine warning icon and tooltip logic for drugs
    // Only show warning for Chewy-prescribed RX items that are not approved
    const shouldShowWarningIconForDrugs = isPrescribable
      ? isChewyPrescriptionTypeForLogic && !isApprovedStatus // Only Chewy + not approved
      : false

    // Warning icon tooltip text based on prescription type
    // For vaccines, always use "Review required"
    // For drugs, use "Missing Approval" for Chewy, "Review required" for non-Chewy
    const warningTooltipText =
      isVaccine && onVaccineClick
        ? t('Common:REVIEW_REQUIRED')
        : isChewyPrescriptionTypeForTooltip
          ? t('Common:MISSING_APPROVAL')
          : t('Common:REVIEW_REQUIRED')

    // Show warning icon only for Chewy-prescribed RX items that are not approved
    const shouldShowWarningIcon: boolean = shouldShowWarningIconForDrugs

    // For Chewy + Approved: show Chewy icon only (no warning)
    const shouldShowChewyIcon =
      displayChewyIcon && isChewyPrescriptionTypeForLogic && isApprovedStatus

    return {
      displayChewyIcon,
      shouldShowChewyIcon,
      shouldShowWarningIcon,
      warningTooltipText,
      isPrescribable,
      isVaccine,
    }
  }, [
    item,
    isRetailOrderLineItem,
    isChewyCheckoutEnabled,
    isApprovedStatus,
    isPrescribable,
    isVaccine,
    onVaccineClick,
    t,
  ])
}
